'use client';

import { logger } from '@magicschool/logger';
import { Button, Icon } from '@magicschool/spellbook';
import type { Enums } from '@magicschool/supabase/types';
import { useStore } from 'features/store';
import capitalize from 'lodash-es/capitalize';
import Image from 'next/image';
import type React from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { DPA_VERSION, PP_VERSION, TOS_VERSION } from '@/features/auth/constants';
import { useLocale } from '@/hooks/useLocale';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';

interface SSOClientProps {
  buttonAction: string;
  next: string;
}

const providerImageMap: Record<Enums<'auth_provider'>, React.ReactNode> = {
  google: <Image src="/icons/social-signin-logos/google.svg" width={20} height={20} alt="" />,
  azure: <Image src="/icons/social-signin-logos/azure.svg" width={23} height={23} alt="" />,
  edlink: <Icon name="key" />,
  email: null,
};

const allProviders: Enums<'auth_provider'>[] = ['google', 'azure', 'email', 'edlink'];

function friendlyProviderName(provider: string): string {
  if (provider === 'edlink') return 'SSO';
  if (provider === 'azure') return 'Microsoft';
  return capitalize(provider);
}

export const SSOClient: React.FC<SSOClientProps> = ({ buttonAction, next }) => {
  const intl = useIntl();
  const [pending, setPending] = useState<Enums<'auth_provider'> | null>(null);
  const { locale } = useLocale();

  const signinWithProvider = useStore(({ AuthStoreData: as }) => as.signinWithProvider, []);

  const onClick = async (provider: Enums<'auth_provider'>) => {
    try {
      setPending(provider);
      analyticsTrack('Signup:ConsentGiven', {
        consentTimestamp: new Date().toISOString(),
        privacyPolicyVersion: PP_VERSION,
        termsVersion: TOS_VERSION,
        dpaVersion: DPA_VERSION,
        location: 'SignupForm',
        authMethod: provider,
      });
      await signinWithProvider(provider, next, locale);
    } catch (err) {
      logger.error('Error signing in with provider', provider, err);
      setPending(null);
    }
  };

  return (
    <div className="flex flex-col gap-2 mt-8 mb-4">
      {allProviders.map((provider) => {
        if (provider === 'email') return null;
        const buttonText = intl.formatMessage(
          { id: 'auth.provider.button' },
          { action: intl.formatMessage({ id: buttonAction }), provider: friendlyProviderName(provider) },
        );

        return (
          <Button
            key={provider}
            size="lg"
            fullWidth
            variant="muted"
            onClick={() => onClick(provider)}
            disabled={pending === provider}
            isLoading={pending === provider}
          >
            <Button.LeadingIcon overrideDefault>{providerImageMap[provider]}</Button.LeadingIcon>
            {buttonText}
          </Button>
        );
      })}
    </div>
  );
};
